import { AfterViewInit, Component, DestroyRef, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';

import { filter, pairwise } from 'rxjs';

import { HttpStatusCodes } from '@constant/http-status-code-constant';
import { StaticValues } from '@constant/static-value-constant';
import { ApiResponse } from '@models/class/api-response';
import { ICountryList } from '@models/interface/country-list-interface';
import { AuthService } from '@services/auth.service';
import { AutoLogoutService } from '@services/auto-logout.service';
import { GlobalStateService } from '@services/global-state-service.';
import { LoaderService } from '@services/loader.service';
import { NavigationService } from '@services/navigation.service';
import { UtilityService } from '@services/utility.service';
import { LoadingComponent } from '@shared/components/loading/loading.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LoadingComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  // Delay (ms) before checking maintenance-mode status, to let the app finish initializing.
  private static readonly MAINTENANCE_CHECK_DELAY_MS = 1000;
  // Delay (ms) before starting the idle-watcher, so it doesn't compete with initial app startup.
  private static readonly IDLE_WATCHER_INIT_DELAY_MS = 1500;
  // Delay (ms) before fetching country/currency data, deprioritized behind more critical startup tasks.
  private static readonly COUNTRY_STATE_FETCH_DELAY_MS = 2000;

  /**
   * Initialization
   */
  storageListener: (event: StorageEvent) => void;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly titleService: Title,
    private readonly autoLogoutService: AutoLogoutService,
    private readonly globalStateService: GlobalStateService,
    private readonly utilityService: UtilityService,
    private readonly navigationService: NavigationService,
    private readonly router: Router,
    private readonly loaderService: LoaderService,
    private readonly authService: AuthService,
    private readonly ngZone: NgZone,
  ) {
    this.loaderService.startLoading(); // Start the loader on app initialization
    this.titleService.setTitle(StaticValues.APP_NAME);
  }

  /*
   * Bootstraps core app behaviours: maintenance-mode check, idle-watcher, country data,
   * router-event loading indicators, URL history tracking, and cross-tab logout synchronisation.
   */
  ngOnInit() {
    // Check for maintenance mode after a short delay to ensure the app has initialized
    this.scheduleBackgroundTask(() => {
      this.navigationService.checkUnderMaintenanceStatus().subscribe({
        next: (response: ApiResponse<unknown>) => {
          if (response?.isSuccess && response?.statusCode === HttpStatusCodes.UNDER_MAINTENANCE) {
            this.router.navigate(['under-maintenance']);
          }
        },
        error: () => {},
      });
    }, AppComponent.MAINTENANCE_CHECK_DELAY_MS);

    if (this.authService.isAuthenticated()) {
      this.scheduleBackgroundTask(() => {
        this.autoLogoutService.initializeIdleWatcher();
      }, AppComponent.IDLE_WATCHER_INIT_DELAY_MS);
    }

    this.scheduleBackgroundTask(() => {
      this.setCountryState();
    }, AppComponent.COUNTRY_STATE_FETCH_DELAY_MS);

    // Listen to router events to show/hide the loader
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loaderService.startLoading();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loaderService.stopLoading();
      } else {
        // No-op for other router event types (e.g. RoutesRecognized, GuardsCheckStart).
      }
    });

    // Set the initial previous and current URLs
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([previous, current]) => {
        if (previous instanceof NavigationEnd) this.utilityService.previousUrl = previous.url;

        if (current instanceof NavigationEnd) this.utilityService.currentUrl = current.url;
      });

    // Listen for storage events to handle logout across tabs.
    // When Tab A logs out, it removes 'webauth' from localStorage.
    // The 'storage' event fires in all OTHER open tabs (Tab B, Tab C, ...).
    // We must clear the local state (including SignalR) in those tabs too.
    this.storageListener = (event: StorageEvent) => {
      if (event.key === 'webauth' && event.newValue == null) {
        // 'webauth' was removed → another tab logged out.
        // Clear storage (stops SignalR connection) and navigate to home in this tab.
        this.authService.clearStorage();
        this.authService.triggerRefresh();
        this.router.navigate(['/'], { queryParams: { refresh: new Date().getTime() } });
      }
    };
    window.addEventListener('storage', this.storageListener);
  }

  /*
   * Stops the global loading spinner once the component view and all child views have been rendered.
   * Called by Angular after the initial rendering pass is complete.
   */
  ngAfterViewInit() {
    this.loaderService.stopLoading(); // Stop the loader after the view has initialized
  }

  /*
   * Fetches the full country list and populates GlobalStateService with both the raw
   * country data and a currencyCode → currencySymbol lookup map.
   * Called once during initialisation so downstream components can read currency
   * symbols without triggering separate API requests.
   */
  setCountryState() {
    this.utilityService.getCountry().subscribe({
      next: countryData => {
        this.globalStateService.countryData = countryData;
        countryData.forEach((item: ICountryList) => {
          this.globalStateService.currencySymbols[item.currencyCode] = item.currencySymbol;
        });
      },
      error: () => {},
    });
  }

  /*
   * Runs a task outside Angular's zone using requestIdleCallback when available,
   * falling back to setTimeout. This defers non-critical initialisation work so it
   * does not block the first render or inflate the initial change-detection cycle.
   */
  private scheduleBackgroundTask(task: () => void, timeout = 1000): void {
    this.ngZone.runOutsideAngular(() => {
      const runTask = () => this.ngZone.run(task);

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        (
          window as Window & {
            requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
          }
        ).requestIdleCallback(
          () => {
            runTask();
          },
          { timeout },
        );
        return;
      }

      globalThis.setTimeout(runTask, 0);
    });
  }

  /*
   * Removes the cross-tab logout storage listener to prevent memory leaks
   * and stale event handling after the root component is torn down.
   */
  ngOnDestroy() {
    // Remove the storage event listener when the component is destroyed
    window.removeEventListener('storage', this.storageListener);
  }
}
